import { useState, useEffect } from 'react';
import SubHeader from '@/pages/profile/components/SubHeader';
import BottomSheet from '@/pages/profile/components/BottomSheet';
import { useToast } from '@/components/base/Toast';
import { supabase } from '@/lib/supabase';

interface Message {
  id: number;
  category: string;
  categoryLabel: string;
  title: string;
  content: string;
  time: string;
  read: boolean;
}

type TabKey = 'all' | 'system' | 'trade';

const tabs: { key: TabKey; label: string }[] = [
  { key: 'all', label: '全部' },
  { key: 'system', label: '系统通知' },
  { key: 'trade', label: '交易提醒' },
];

export default function MessagesPage() {
  const { showToast } = useToast();
  const [activeTab, setActiveTab] = useState<TabKey>('all');
  const [list, setList] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Message | null>(null);

  const fetchData = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const userId = sessionData.session?.user?.id;

      if (!userId) {
        setList([]);
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('user_id', userId)
        .order('id', { ascending: false });

      if (error) throw error;

      setList(
        (data || []).map((row) => ({
          id: row.id,
          category: row.category || 'system',
          categoryLabel: row.category_label || '系统通知',
          title: row.title || '',
          content: row.content || '',
          time: row.time || '',
          read: row.read ?? false,
        })),
      );
    } catch {
      showToast('消息加载失败', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = activeTab === 'all' ? list : list.filter((m) => m.category === activeTab);
  const unreadCount = list.filter((m) => !m.read).length;

  const openMessage = async (msg: Message) => {
    if (!msg.read) {
      await supabase.from('messages').update({ read: true }).eq('id', msg.id);
      setList((prev) => prev.map((m) => (m.id === msg.id ? { ...m, read: true } : m)));
    }
    setSelected({ ...msg, read: true });
  };

  const markAllRead = async () => {
    await supabase.from('messages').update({ read: true }).eq('read', false);
    setList((prev) => prev.map((m) => ({ ...m, read: true })));
    showToast('已全部标记为已读');
  };

  return (
    <div className="min-h-screen bg-brown-50 pb-20">
      <SubHeader
        title="消息中心"
        right={
          unreadCount > 0 ? (
            <button
              onClick={markAllRead}
              className="text-gold-400 text-xs cursor-pointer whitespace-nowrap"
            >
              全部已读
            </button>
          ) : undefined
        }
      />

      {/* Tabs */}
      <div className="bg-white px-4 py-3 border-b border-brown-50">
        <div className="flex gap-2">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`relative px-4 py-1.5 rounded-full text-xs font-medium cursor-pointer whitespace-nowrap transition-colors ${
                activeTab === tab.key
                  ? 'bg-brown-900 text-gold-400'
                  : 'bg-brown-50 text-brown-500'
              }`}
            >
              {tab.label}
              {tab.key === 'all' && unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center">
                  {unreadCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Message List */}
      <div className="px-4 mt-3 space-y-3">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <span className="w-6 h-6 border-2 border-gold-500/30 border-t-gold-500 rounded-full animate-spin"></span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-brown-400 text-sm">暂无消息</div>
        ) : (
          filtered.map((msg) => (
            <button
              key={msg.id}
              onClick={() => openMessage(msg)}
              className="w-full bg-white rounded-2xl p-4 shadow-sm text-left cursor-pointer active:scale-[0.98] transition-transform animate-fade-up"
            >
              <div className="flex items-start gap-3">
                <div
                  className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    msg.category === 'system' ? 'bg-gold-50' : 'bg-brown-100'
                  }`}
                >
                  <span
                    className={`w-5 h-5 flex items-center justify-center ${
                      msg.category === 'system' ? 'text-gold-600' : 'text-brown-600'
                    }`}
                  >
                    <i className={msg.category === 'system' ? 'ri-notification-3-line' : 'ri-exchange-funds-line'}></i>
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-brown-900 text-sm font-semibold truncate flex-1 mr-2">
                      {msg.title}
                    </span>
                    {!msg.read && <span className="w-2 h-2 rounded-full bg-red-500 flex-shrink-0"></span>}
                  </div>
                  <p className="text-brown-500 text-xs line-clamp-2 leading-relaxed">{msg.content}</p>
                  <div className="flex items-center gap-1 mt-2 text-brown-400 text-[10px]">
                    <span className="w-3 h-3 flex items-center justify-center">
                      <i className="ri-time-line"></i>
                    </span>
                    <span>{msg.time}</span>
                    <span className="mx-1">·</span>
                    <span>{msg.categoryLabel}</span>
                  </div>
                </div>
              </div>
            </button>
          ))
        )}
      </div>

      {/* Detail Sheet */}
      <BottomSheet open={!!selected} onClose={() => setSelected(null)} title="消息详情">
        {selected && (
          <div className="px-4 pb-8">
            <div className="flex items-center gap-2 mb-4">
              <span className="bg-gold-100 text-gold-700 text-[10px] px-2 py-0.5 rounded-full">
                {selected.categoryLabel}
              </span>
              <span className="text-brown-400 text-xs">{selected.time}</span>
            </div>
            <h2 className="text-brown-900 text-lg font-bold mb-4">{selected.title}</h2>
            <p className="text-brown-600 text-sm leading-relaxed">{selected.content}</p>
          </div>
        )}
      </BottomSheet>
    </div>
  );
}